import { getGetApiBotProviderListQueryKey, BotProvider, usePutApiBotProviderDelete, usePutApiBotProviderRename, postApiBotProviderExport, BotProviderType } from "@/API";
import { ActionIcon, Avatar, Badge, Button, Group, Paper, Stack, Text } from "@mantine/core";
import { IconBrandLine, IconEdit, IconFileExport, IconTrash, IconUser } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "@tanstack/react-router";
import { modals } from "@mantine/modals";
import { ChatBotDataLink } from "@/Workspaces/Components/ChatBotDataLink";
import { TextSaveToFile } from "@/Helpers/SaveToFile";
import { BOT_PROVIDER_METADATA_KEYS } from "./Web/constants";
import { GetWebBotAvatarUrl } from "@/APIURL";
import z from "zod/v4";

export function BotProviderCard(props: { botProvider: BotProvider }) {
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const rename_Mutation = usePutApiBotProviderRename()
    const delete_Mutation = usePutApiBotProviderDelete()

    const avatarSrc = props.botProvider.metadata?.[BOT_PROVIDER_METADATA_KEYS.avatarIconName]
        ? GetWebBotAvatarUrl(props.botProvider.id ?? "")
        : undefined;

    return <>
        <Paper pos="relative" shadow="xs" p="sm" withBorder w={300}>
            <Group pos="absolute" right={5} top={5} gap={0}>
                <ActionIcon variant="subtle" onClick={async () => {
                    var exportData = await postApiBotProviderExport({ workspaceId: props.botProvider.workspaceId, id: props.botProvider.id })
                    TextSaveToFile(`${exportData.data.name}.json`, JSON.stringify(exportData.data))
                }}>
                    <IconFileExport style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={async () => {
                    modals.openContextModal({
                        modal: "ConfirmCountDown",
                        title: '刪除',
                        innerProps: {
                            countDownSecond: 1,
                            children: (
                                <Text size="sm">
                                    是否要刪除"{props.botProvider.name}"?
                                </Text>
                            ),
                            labels: {
                                confirm: '刪除', cancel: '取消'
                            },
                            confirmProps: { color: 'red' },
                            onConfirm: async () => {
                                try {
                                    await delete_Mutation.mutateAsync({
                                        params: {
                                            workspaceId: props.botProvider.workspaceId,
                                            id: props.botProvider.id
                                        }
                                    })
                                    queryClient.invalidateQueries({ queryKey: getGetApiBotProviderListQueryKey({ workspaceId: props.botProvider.workspaceId }) })
                                    notifications.show({ message: "刪除成功" })
                                } catch (e) {
                                    notifications.show({ message: "刪除失敗", color: "red" })
                                }
                            },
                        },

                    });
                }}>
                    <IconTrash style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon>
            </Group>
            <Group align="center" gap="sm" mb="xs" pr={60} wrap="nowrap">
                <Avatar src={avatarSrc} radius="xl" size={44} color={props.botProvider.type === BotProviderType.Line ? "green" : undefined}>
                    {props.botProvider.type === BotProviderType.Line ? <IconBrandLine size={24} color="#06C755" /> : <IconUser size={24} />}
                </Avatar>
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                    <div>
                        <Badge size="sm" color={props.botProvider.type === BotProviderType.Line ? "green" : undefined}>
                            {props.botProvider.type}
                        </Badge>
                    </div>
                    <Group gap={4} wrap="nowrap">
                        <Text fw={600} size="md" truncate="end">{props.botProvider.name}</Text>
                        <ActionIcon size="sm" variant="subtle" onClick={() => {
                            modals.openContextModal({
                                modal: "TextInput",
                                title: "修改名稱",
                                innerProps: {
                                    initText: props.botProvider.name ?? "",
                                    zodString: z.string().nullable().optional() as any,
                                    onConfirm: async v => {
                                        try {
                                            await rename_Mutation.mutateAsync({
                                                params: {
                                                    workspaceId: props.botProvider.workspaceId,
                                                    id: props.botProvider.id,
                                                    name: v
                                                },
                                            })
                                            queryClient.invalidateQueries({ queryKey: getGetApiBotProviderListQueryKey({ workspaceId: props.botProvider.workspaceId }) })
                                            notifications.show({ message: "更新成功" })
                                        } catch (e) {
                                            notifications.show({ message: "更新失敗", color: "red" })
                                        }
                                    }
                                }
                            })
                        }} >
                            <IconEdit style={{ width: '70%', height: '70%' }} stroke={1.5} />
                        </ActionIcon>
                    </Group>
                </Stack>
            </Group>
            <Text>連接機器人</Text>
            <ChatBotDataLink workspaceId={props.botProvider.workspaceId ?? ""} chatBotDataId={props.botProvider.chatBotDataId ?? ""} />
            <Button mt="sm" fullWidth onClick={() => {
                navigate({
                    to: "/workspace/$workspaceId/botProvider/$botProviderId", params: {
                        workspaceId: props.botProvider.workspaceId ?? "",
                        botProviderId: props.botProvider.id ?? "",
                    }
                })
            }}>開啟設定</Button>
            <Group mt={5} grow wrap="nowrap">
                <Button variant="outline" onClick={() => {
                    navigate({
                        to: "/workspace/$workspaceId/botProvider/$botProviderId/history", params: {
                            workspaceId: props.botProvider.workspaceId ?? "",
                            botProviderId: props.botProvider.id ?? "",
                        }
                    })
                }}>聊天紀錄</Button>
                {props.botProvider.type === BotProviderType.Web && (
                    <Button variant="outline" color="teal" onClick={() => {
                        navigate({
                            to: "/workspace/$workspaceId/botProvider/$botProviderId/feedback", params: {
                                workspaceId: props.botProvider.workspaceId ?? "",
                                botProviderId: props.botProvider.id ?? "",
                            }
                        })
                    }}>使用者回饋</Button>
                )}
            </Group>
        </Paper >
    </>
}